import { useCallback, useState, DragEvent } from 'react';

import { IconFilePlusInside, IconUpload } from '@iwoca/icons';
import { PostDocumentUploadResponse } from '@iwoca/lapi-client/edge';
import { clsx } from 'clsx';
import { v4 } from 'uuid';

import styles from './DragDropUpload.module.css';
import { fetchSaveUploadedDocuments } from '../../../../../api/lendingApi/lendingApiQueries';
import { useContextStateKey } from '../../../../../hooks/useContextStateKey';
import { TDocumentTypeDE } from '../../../../../lapiOverrideTypesDE';
import { TUploadRejectReasonCode } from '../../../UploadButton/UploadButton';
import {
  trackDocumentUploadClicked,
  trackDocumentUploaded,
} from '../../../UploadButton/UploadButton.tracking.helper';

export type TDocumentType =
  PostDocumentUploadResponse['data']['document_type'] | TDocumentTypeDE;

export type TUploadResponse = {
  errors?: Array<{ code: string; detail: string }>;
  attributes?: {
    status: string;
    reject_reason?: string;
  };
};

type TProps = {
  documentType: TDocumentType;
  description?: string;
  listOfInstructions: React.ReactNode[];
  onUpload?: (stateKey: string, body: FormData) => Promise<TUploadResponse>;
  setFileLoading: (id: string, name: string) => void;
  setFileSuccess: (id: string) => void;
  setFileError: (id: string, error: { code: string; detail: string }) => void;
  setFileReject: (id: string, rejectReason: TUploadRejectReasonCode) => void;
  isError?: boolean;
};
const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png'];

const isValidFileType = (file: File): boolean => {
  const fileName = file.name.toLowerCase();
  return ALLOWED_EXTENSIONS.some((ext) => fileName.endsWith(ext));
};

const DragDropUpload = ({
  documentType = 'other',
  description,
  listOfInstructions,
  onUpload,
  setFileLoading,
  setFileSuccess,
  setFileError,
  setFileReject,
  isError = false,
}: TProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const stateKey = useContextStateKey();

  const handleFiles = useCallback(
    async (files: FileList) => {
      if (!stateKey) return;

      const fileArray = Array.from(files);

      // Upload all files in parallel
      await Promise.all(
        fileArray.map(async (file) => {
          const uuid = v4();
          setFileLoading(uuid, file.name);

          if (!isValidFileType(file)) {
            setFileError(uuid, {
              code: 'InvalidFileType',
              detail: 'File format incorrect',
            });
            return;
          }

          const body = new FormData();
          body.append('document', file);
          body.append('document_type', documentType);

          try {
            const response = onUpload
              ? await onUpload(stateKey, body)
              : await fetchSaveUploadedDocuments({ stateKey, body });

            if (response?.errors?.[0]) {
              setFileError(uuid, response.errors[0]);
            } else if (response?.attributes?.status === 'rejected') {
              setFileReject(uuid, response?.attributes?.reject_reason);
            } else {
              trackDocumentUploaded(documentType);
              setFileSuccess(uuid);
            }
          } catch {
            setFileError(uuid, {
              code: 'UploadFailed',
              detail: 'Upload failed',
            });
          }
        }),
      );

      // Refetch happens automatically in mutation onSuccess (when onUpload is provided)
    },
    [
      stateKey,
      onUpload,
      setFileLoading,
      setFileSuccess,
      setFileError,
      setFileReject,
      documentType,
    ],
  );

  const handleDragOver = useCallback((e: DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLLabelElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const { files } = e.dataTransfer;
      if (files?.length) {
        handleFiles(files);
      }
    },
    [handleFiles],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    trackDocumentUploadClicked(documentType);
    handleFiles(e.target.files);
    e.target.value = '';
  };

  return (
    <div className={styles.container}>
      <label
        htmlFor={`drag-drop-input-${documentType}`}
        className={clsx(styles.dropzone, {
          [styles.dropzoneActive]: isDragging,
          [styles.dropzoneError]: isError,
        })}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        data-testid="drag-drop-zone"
      >
        <div className={styles.uploadRow}>
          {isDragging ? (
            <div className={styles.uploadButton}>
              <IconFilePlusInside className={styles.uploadIcon} />
              <span className={styles.uploadText}>Drop to upload</span>
            </div>
          ) : (
            <>
              <div className={styles.uploadButton}>
                <IconUpload className={styles.uploadIcon} />
                <span className={styles.uploadText}>Upload</span>
              </div>
              <span className={styles.dropText}>or drag & drop files here</span>
            </>
          )}
        </div>

        <div className={styles.instructionsContainer}>
          {description && <p className={styles.description}>{description}</p>}
          <ul className={styles.requirementsList}>
            {listOfInstructions.map((instruction, index) => (
              <li key={index}>{instruction}</li>
            ))}
          </ul>
        </div>

        <input
          id={`drag-drop-input-${documentType}`}
          data-testid="drag-drop-input"
          multiple
          hidden
          type="file"
          accept=".jpg, .jpeg, .png, .pdf"
          onChange={handleInputChange}
          className="fs-mask"
        />
      </label>
    </div>
  );
};

export default DragDropUpload;
